import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=1800&q=80";

const lines = ["ANTS", "DON'T", "SLEEP"];

const ease = [0.16, 1, 0.3, 1];

function MaskedLine({ text, index }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, ease, delay: 0.45 + index * 0.14 }}
        className="block font-mono-x font-extrabold uppercase leading-[0.86] tracking-tighter text-[19vw] md:text-[15vw] lg:text-[13vw]"
      >
        <span className={index === 2 ? "glitch text-[#ff3b1d]" : "glitch"} data-text={text}>
          {text}
        </span>
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden border-b border-white/15"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="DJ performing in a dark underground club"
          className="w-full h-full object-cover grayscale contrast-125"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 scanline" />
      </motion.div>

      <motion.div
        style={{ y: fgY, opacity: fade }}
        className="relative z-10 h-full flex flex-col justify-between px-4 sm:px-8 pt-24 pb-8"
      >
        <div className="flex justify-between font-mono-x text-[10px] sm:text-[11px] tracking-[0.25em] text-white/50 uppercase">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.8 }}
            data-testid="hero-tag-left"
          >
            EST. UNDERGROUND / NO GENRE
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 0.8 }}
            className="text-right"
            data-testid="hero-tag-right"
          >
            DJ — PRODUCER — COLLECTIVE
          </motion.span>
        </div>

        <h1 className="select-none" data-testid="hero-title">
          {lines.map((l, i) => (
            <MaskedLine key={l} text={l} index={i} />
          ))}
        </h1>

        <div className="flex items-end justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.9, ease }}
            className="max-w-md font-mono-x text-[11px] sm:text-xs leading-relaxed tracking-wider text-white/60 uppercase"
            data-testid="hero-subtext"
          >
            Raw techno. Broken machines. Sets that run until the lights come on.
            <span className="text-[#ff3b1d] blink ml-2">_</span>
          </motion.p>
          <motion.a
            href="#manifesto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 0.8 }}
            data-testid="hero-scroll-cue"
            className="hidden sm:flex flex-col items-center gap-3 font-mono-x text-[10px] tracking-[0.3em] text-white/50 hover:text-white transition-colors duration-200"
          >
            SCROLL
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown size={16} />
            </motion.span>
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
}
